import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { EmployeeWithLatestAndDraftEvaluation, ScoreGridDto, ScoreGoalType, ScoreGoalDto, Country } from '../../types'
import { PerModal } from '../../components/custom_components/Modal'
import PerText from '../../components/custom_components/Text'
import { computeFullname } from '../../shared/nameDisplayPreferencesUtil'
import { useAuth } from '../../context/auth-context'
import PerFlexRow from '../../components/custom_components/FlexRow'
import { Col, Card } from 'antd'
import PerSpacer from '../../components/custom_components/Spacer'
import { useLatestScoreGridByEmployee } from '../../graphql/draft-score-grid/useDraftScoreGrid'
import { LoadingAndErrors } from '../../components/custom_components/LoadingAndErrors'
import { LatestScoreGoalModalRow } from './ScoreGoalModalRow'

type LatestScoreGridsModalProps = {
  onCancel: () => void
  employeeData: EmployeeWithLatestAndDraftEvaluation
  companyId: string
  country: Country
}

export const LatestScoreGridsModal: FC<LatestScoreGridsModalProps> = ({ onCancel, employeeData, companyId, country }) => {
  const { data, error, loading } = useLatestScoreGridByEmployee(companyId, country, employeeData.employee.id)

  if (error) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data} error={error} />
  }

  return (
    <>
      {data && (
        <LatestScoreGridsModalContent
          visible={!!data}
          onCancel={onCancel}
          employeeData={employeeData}
          scoreGrid={data.getLatestScoreGridByEmployee}
        />
      )}
    </>
  )
}

type LatestScoreGridsModalContentProps = {
  visible: boolean
  onCancel: () => void
  employeeData: EmployeeWithLatestAndDraftEvaluation
  scoreGrid: ScoreGridDto | null | undefined
}

export const LatestScoreGridsModalContent: FC<LatestScoreGridsModalContentProps> = ({ visible, onCancel, employeeData, scoreGrid }) => {
  const [t] = useTranslation()
  const { user } = useAuth()

  const getScoreGoal = (type: ScoreGoalType): number | undefined => {
    const scoreGoal: ScoreGoalDto | undefined = scoreGrid ? scoreGrid.scoreGoals.find((sg) => sg.viewId === type) : undefined
    return scoreGoal ? scoreGoal.resultEvaluation! : undefined
  }

  return (
    <>
      <PerModal
        size="large"
        title={{
          heading: t('latestScoreGrid.modal.heading', {
            employeeFullName: computeFullname(user.nameDisplayPreference, employeeData.employee.firstName, employeeData.employee.lastName),
          }),
        }}
        visible={visible}
        footer={{
          cancelBtn: {
            onClick: () => {
              onCancel()
            },
          },
        }}
        onCancel={() => {
          onCancel()
        }}
      >
        <PerFlexRow align="middle">
          <Col span={12}>
            <PerFlexRow direction="column">
              <Card>
                <PerFlexRow align="middle" justify="center" mb="xs">
                  <div className="tab-bar__tab tab-bar__tab--active" style={{ paddingTop: 0, cursor: 'default' }}>
                    <PerText>{t('ownGrowthView.grid.category.Customer')}</PerText>
                  </div>
                </PerFlexRow>
                <LatestScoreGoalModalRow type={ScoreGoalType.Customer_1} value={getScoreGoal(ScoreGoalType.Customer_1)} mb="xs" />
                <LatestScoreGoalModalRow type={ScoreGoalType.Customer_2} value={getScoreGoal(ScoreGoalType.Customer_2)} mb="xs" />
                <LatestScoreGoalModalRow type={ScoreGoalType.Customer_3} value={getScoreGoal(ScoreGoalType.Customer_3)} mb="xs" />
                <LatestScoreGoalModalRow type={ScoreGoalType.Customer_4} value={getScoreGoal(ScoreGoalType.Customer_4)} mb="xs" />
                <LatestScoreGoalModalRow type={ScoreGoalType.Customer_5} value={getScoreGoal(ScoreGoalType.Customer_5)} />
              </Card>

              <PerSpacer mb="s" />

              <Card>
                <PerFlexRow align="middle" justify="center" mb="xs">
                  <div className="tab-bar__tab tab-bar__tab--active" style={{ paddingTop: 0, cursor: 'default' }}>
                    <PerText>{t('ownGrowthView.grid.category.Innovation')}</PerText>
                  </div>
                </PerFlexRow>
                <LatestScoreGoalModalRow type={ScoreGoalType.Innovation_1} value={getScoreGoal(ScoreGoalType.Innovation_1)} mb="xs" />
                <LatestScoreGoalModalRow type={ScoreGoalType.Innovation_2} value={getScoreGoal(ScoreGoalType.Innovation_2)} />
              </Card>

              <PerSpacer mb="s" />

              <Card>
                <PerFlexRow align="middle" justify="center" mb="xs">
                  <div className="tab-bar__tab tab-bar__tab--active" style={{ paddingTop: 0, cursor: 'default' }}>
                    <PerText>{t('ownGrowthView.grid.category.HumanValues')}</PerText>
                  </div>
                </PerFlexRow>
                <LatestScoreGoalModalRow type={ScoreGoalType.HumanValues_1} value={getScoreGoal(ScoreGoalType.HumanValues_1)} mb="xs" />
                <LatestScoreGoalModalRow type={ScoreGoalType.HumanValues_2} value={getScoreGoal(ScoreGoalType.HumanValues_2)} />
              </Card>
            </PerFlexRow>
          </Col>

          <Col span={12}>
            <PerFlexRow direction="column">
              <Card>
                <PerFlexRow align="middle" justify="center" mb="xs">
                  <div className="tab-bar__tab tab-bar__tab--active" style={{ paddingTop: 0, cursor: 'default' }}>
                    <PerText>{t('ownGrowthView.grid.category.Speed')}</PerText>
                  </div>
                </PerFlexRow>
                <LatestScoreGoalModalRow type={ScoreGoalType.Speed_1} value={getScoreGoal(ScoreGoalType.Speed_1)} mb="xs" />
                <LatestScoreGoalModalRow type={ScoreGoalType.Speed_2} value={getScoreGoal(ScoreGoalType.Speed_2)} mb="xs" />
                <LatestScoreGoalModalRow type={ScoreGoalType.Speed_3} value={getScoreGoal(ScoreGoalType.Speed_3)} />
              </Card>

              <PerSpacer mb="s" />

              <Card>
                <PerFlexRow align="middle" justify="center" mb="xs">
                  <div className="tab-bar__tab tab-bar__tab--active" style={{ paddingTop: 0, cursor: 'default' }}>
                    <PerText>{t('ownGrowthView.grid.category.Teamwork')}</PerText>
                  </div>
                </PerFlexRow>
                <LatestScoreGoalModalRow type={ScoreGoalType.Teamwork_1} value={getScoreGoal(ScoreGoalType.Teamwork_1)} mb="xs" />
                <LatestScoreGoalModalRow type={ScoreGoalType.Teamwork_2} value={getScoreGoal(ScoreGoalType.Teamwork_2)} mb="xs" />
                <LatestScoreGoalModalRow type={ScoreGoalType.Teamwork_3} value={getScoreGoal(ScoreGoalType.Teamwork_3)} />
              </Card>

              <PerSpacer mb="s" />

              <Card>
                <PerFlexRow align="middle" justify="center" mb="xs">
                  <div className="tab-bar__tab tab-bar__tab--active" style={{ paddingTop: 0, cursor: 'default' }}>
                    <PerText>{t('ownGrowthView.grid.category.Excellence')}</PerText>
                  </div>
                </PerFlexRow>
                <LatestScoreGoalModalRow type={ScoreGoalType.Excellence_1} value={getScoreGoal(ScoreGoalType.Excellence_1)} mb="xs" />{' '}
                <LatestScoreGoalModalRow type={ScoreGoalType.Excellence_2} value={getScoreGoal(ScoreGoalType.Excellence_2)} mb="xs" />
                <LatestScoreGoalModalRow type={ScoreGoalType.Excellence_3} value={getScoreGoal(ScoreGoalType.Excellence_3)} />
              </Card>
            </PerFlexRow>
          </Col>
        </PerFlexRow>
      </PerModal>
    </>
  )
}
